// Keyboard, mouse and touch state. Looking around uses pointer lock when the
// browser grants it and plain dragging otherwise; on a phone, a finger dragged
// across the view looks around and two fingers pinch to zoom.
export class Input {
  constructor(canvas) {
    this.canvas = canvas;
    this.keys = new Set();
    this.pressed = new Set(); // keys that went down since the last frame
    this.lookX = 0;
    this.lookY = 0;
    this.wheel = 0;
    this.dragging = false;
    this.clicked = false; // the left button went down since the last frame
    this.enabled = true;
    this.stick = { x: 0, z: 0 }; // the on-screen stick of a phone: right and forward, -1..1
    this.fingers = new Map(); // touches on the view: id -> { x, y }

    const fingerDown = (e) => {
      e.preventDefault(); // no pretend mouse clicks afterwards
      for (const t of e.changedTouches) this.fingers.set(t.identifier, { x: t.clientX, y: t.clientY });
    };
    const fingerMove = (e) => {
      e.preventDefault();
      if (!this.enabled) return;
      const gap = () => {
        const [a, b] = [...this.fingers.values()];
        return Math.hypot(a.x - b.x, a.y - b.y);
      };
      const before = this.fingers.size === 2 ? gap() : 0;
      for (const t of e.changedTouches) {
        const f = this.fingers.get(t.identifier);
        if (!f) continue;
        if (this.fingers.size === 1) {
          this.lookX += (t.clientX - f.x) * 1.6;
          this.lookY += (t.clientY - f.y) * 1.6;
        }
        f.x = t.clientX;
        f.y = t.clientY;
      }
      if (this.fingers.size === 2) this.wheel -= (gap() - before) * 4;
    };
    const fingerUp = (e) => {
      for (const t of e.changedTouches) this.fingers.delete(t.identifier);
    };
    canvas.addEventListener('touchstart', fingerDown, { passive: false });
    canvas.addEventListener('touchmove', fingerMove, { passive: false });
    canvas.addEventListener('touchend', fingerUp);
    canvas.addEventListener('touchcancel', fingerUp);

    window.addEventListener('keydown', (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (!this.keys.has(k)) this.pressed.add(k);
      this.keys.add(k);
      if (k === ' ' || k === 'Tab' || k.startsWith('Arrow')) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => {
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      this.keys.delete(k);
    });
    window.addEventListener('blur', () => this.keys.clear());

    canvas.addEventListener('mousedown', (e) => {
      if (!this.enabled) return;
      this.dragging = true;
      if (e.button === 0) this.clicked = true;
      if (e.button === 0 && document.pointerLockElement !== canvas && canvas.requestPointerLock) {
        const p = canvas.requestPointerLock();
        if (p && p.catch) p.catch(() => {});
      }
    });
    window.addEventListener('mouseup', () => {
      this.dragging = false;
    });
    window.addEventListener('mousemove', (e) => {
      if (!this.enabled) return;
      if (document.pointerLockElement === canvas || this.dragging) {
        this.lookX += e.movementX;
        this.lookY += e.movementY;
      }
    });
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.wheel += e.deltaY;
    }, { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  down(k) {
    return this.keys.has(k);
  }

  // An on-screen button going down and coming up, as if it were the key.
  press(k) {
    if (!this.enabled) return;
    if (!this.keys.has(k)) this.pressed.add(k);
    this.keys.add(k);
  }

  release(k) {
    this.keys.delete(k);
  }

  // True once per key press.
  hit(k) {
    return this.pressed.has(k);
  }

  // Movement intent from WASD, the arrow keys or the on-screen stick:
  // { x: right, z: forward }, each -1..1.
  move() {
    const x = (this.down('d') || this.down('ArrowRight') ? 1 : 0) - (this.down('a') || this.down('ArrowLeft') ? 1 : 0);
    const z = (this.down('w') || this.down('ArrowUp') ? 1 : 0) - (this.down('s') || this.down('ArrowDown') ? 1 : 0);
    if (x === 0 && z === 0 && this.enabled && Math.hypot(this.stick.x, this.stick.z) > 0.18) return { x: this.stick.x, z: this.stick.z };
    return { x, z };
  }

  // Call at the end of each frame.
  endFrame() {
    this.pressed.clear();
    this.clicked = false;
    this.lookX = 0;
    this.lookY = 0;
    this.wheel = 0;
  }

  releasePointer() {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }
}
